import { useState } from "react";
import { deleteHttp, post } from "@common/api/apiShared";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { useExternalEntitiesQuery } from "../../../processingActivity/hooks/useGroupEntitiesQuery";
import { LegalEntitySubContractorsFieldProps } from "./LegalEntitySubContractorsField";

interface SubContractorSaveModel {
    subContractorIds: Array<string>;
    updateSubContractors: boolean;
}

interface SubContractorRemoveModel {
    subContractorId: string;
    updateSubContractors: boolean;
}

export function useLegalEntitySubContractorsField(props: LegalEntitySubContractorsFieldProps) {
    const [subContractorForDeletion, setSubContractorForDeletion] = useState<{ id: string; name: string } | undefined>(undefined);
    const [alsoRemoveFromProcessingActivities, setAlsoRemoveFromProcessingActivities] = useState(false);

    const { allExternalEntities, isLoading } = useExternalEntitiesQuery(!props.isPreview);

    const addMutation = useDotLegalMutation((saveModel: SubContractorSaveModel) =>
        post<SubContractorSaveModel>(`/legalEntitySubContractor/${props.legalEntityId}`, saveModel)
    );

    const removeMutation = useDotLegalMutation((removeModel: SubContractorRemoveModel) =>
        deleteHttp(`/legalEntitySubContractor/${props.legalEntityId}/${removeModel.subContractorId}?updateSubContractors=${removeModel.updateSubContractors}`)
    );

    // A supplier cannot be its own sub contractor, so it never offers itself in the selector.
    const selectableLegalEntities = allExternalEntities?.filter((x) => x.id !== props.legalEntityId) ?? [];

    // Editing only works when the page has handed in a way to reload the legal entity afterwards, so the
    // template editor preview leaving that out is what keeps the table read only there.
    const isEditable = props.onSubContractorsChanged !== undefined && !props.readOnly && !props.disabled;

    async function onSubContractorsChange(subContractorIds: Array<string>, alsoAddToProcessingActivities?: boolean) {
        const removedIds = props.subContractorIds.filter((x) => !subContractorIds.includes(x));

        if (removedIds.length > 0) {
            askBeforeRemoving(removedIds[0]);
            return;
        }

        if (subContractorIds.some((x) => !props.subContractorIds.includes(x))) {
            await addSubContractors(subContractorIds, alsoAddToProcessingActivities ?? false);
        }
    }

    async function addSubContractors(subContractorIds: Array<string>, alsoAddToProcessingActivities: boolean) {
        await addMutation.mutateAsync({ subContractorIds: subContractorIds, updateSubContractors: alsoAddToProcessingActivities });
        props.onSubContractorsChanged?.();
    }

    function askBeforeRemoving(removedId: string) {
        const removed = allExternalEntities?.find((x) => x.id === removedId);

        setSubContractorForDeletion({ id: removedId, name: removed?.name ?? "" });
    }

    async function onRemoveSubContractorConfirmed() {
        const removedId = subContractorForDeletion!.id;

        await removeMutation.mutateAsync({ subContractorId: removedId, updateSubContractors: alsoRemoveFromProcessingActivities });
        props.onSubContractorsChanged?.();

        closeRemoveDialog();
    }

    function closeRemoveDialog() {
        setSubContractorForDeletion(undefined);
        setAlsoRemoveFromProcessingActivities(false);
    }

    return {
        selectableLegalEntities,
        isLoading,
        isEditable,
        onSubContractorsChange: isEditable ? onSubContractorsChange : undefined,
        subContractorForDeletion,
        onRemoveSubContractorConfirmed,
        closeRemoveDialog,
        alsoRemoveFromProcessingActivities,
        setAlsoRemoveFromProcessingActivities,
        isRemoving: removeMutation.isLoading,
    };
}
